import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowBack, Close, CloudUpload, Delete, Edit, Save } from "@mui/icons-material";
import {
    Box,
    Button,
    Card,
    CardContent,
    CardMedia,
    Chip,
    Container,
    Divider,
    IconButton,
    Paper,
    Stack,
    TextField,
    Typography
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useEffect, useRef, useState } from "react";
import { Controller, set, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { z } from "zod";
import api from "../../../../api";
import CustomMDEditor from "../../../../components/CustomMD/CustomMDEditor";
import CustomMDPreview from "../../../../components/CustomMD/CustomMDPreview";
import PlaceHolderImage from "../../../../components/svg/PlaceHolderImage";
import useLoading from "../../../../context/Loading/useLoading";
import { getBackendUrl, getServerImage } from "../../../../util/envFn";
import { getNullableString } from "../../../../util/string";
import { Portfolio as PortfolioType } from "./types";
import ConfirmationDialog from "../../../../components/ConfirmationDialog/ConfirmationDialog";
import { useLogin } from "../../../../context/Login/LoginContext";

const portfolioSchema = z.object({
    title: z.string().min(1, "Title is required"),
    description: z.string().min(1, "Description is required"),
    visualContent: z.string().optional(),
    yourRole: z.string().optional(),
    skills: z.string().optional(),
    situation: z.string().optional(),
    task: z.string().optional(),
    action: z.string().optional(),
    result: z.string().optional(),
    isDraft: z.boolean(),
});

type PortfolioFormData = z.infer<typeof portfolioSchema>;

const Portfolio = () => {
    const { id } = useParams();
    const [portfolio, setPortfolio] = useState<PortfolioType | null>(null);
    const [editMode, setEditMode] = useState<boolean>(false);
    const { setLoading } = useLoading();
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const [previewImage, setPreviewImage] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
    const { user } = useLogin();
    const isAdmin = user?.role === "admin";
    const isOwner = user?._id === portfolio?.userId;

    // React Hook Form setup
    const { control, handleSubmit, formState: { errors }, reset, setValue } = useForm<PortfolioFormData>({
        resolver: zodResolver(portfolioSchema),
        defaultValues: {
            title: "",
            description: "",
            visualContent: "",
            yourRole: "",
            skills: "",
            isDraft: true,
            situation: "",
            task: "",
            action: "",
            result: "",
        }
    });

    // Handle file upload
    const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!event.target.files || event.target.files.length === 0) {
            return;
        }

        const file = event.target.files[0];
        const formData = new FormData();
        formData.append('portfolioImage', file);

        setLoading(true);
        try {
            const response = await api.post(getBackendUrl('/upload/portfolio-image'), formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            if (response.data.success) {
                // Use url instead of path for proper image access
                const imageUrl = response.data.file.url;
                setValue('visualContent', imageUrl);

                // Show preview using getServerImage to properly format the URL
                setPreviewImage(getServerImage(imageUrl));

                enqueueSnackbar('Image uploaded successfully', { variant: 'success' });
            }
        } catch (error) {
            console.error('Error uploading image:', error);
            enqueueSnackbar('Failed to upload image', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const triggerFileInput = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    useEffect(() => {
        setLoading(true);
        api.get(getBackendUrl(`/user-portfolio/${id}`))
            .then(res => {
                setPortfolio(res.data ?? null);
                // Set preview image if exists
                if (res.data && res.data.visualContent) {
                    setPreviewImage(res.data.visualContent);
                }
                // Set form default values when portfolio data is fetched
                if (res.data) {
                    reset({
                        title: res.data.title || "",
                        description: res.data.description || "",
                        visualContent: res.data.visualContent || "",
                        yourRole: res.data.yourRole || "",
                        skills: res.data.skills || "",
                        isDraft: res.data.isDraft,
                        situation: res.data.situation || "",
                        task: res.data.task || "",
                        action: res.data.action || "",
                        result: res.data.result || "",
                    });
                }
            })
            .catch(err => {
                console.error(err);
                enqueueSnackbar("Failed to fetch portfolios", { variant: "error" });
            })
            .finally(() => {
                setLoading(false);
            });
    }, [id, setLoading, reset, enqueueSnackbar]);

    const onSubmit = (data: PortfolioFormData) => {
        setLoading(true);
        api.patch(getBackendUrl(`/user-portfolio/${id}`), data)
            .then(res => {
                const data = {
                    ...res.data,
                    visualContent: previewImage
                }
                setPortfolio(data);
                setEditMode(false);
                enqueueSnackbar("Portfolio updated successfully", { variant: "success" });
            })
            .catch(err => {
                console.error(err);
                enqueueSnackbar("Failed to update portfolio", { variant: "error" });
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const cancelEdit = () => {
        setEditMode(false);
        // Reset form to original values
        if (portfolio) {
            reset({
                title: portfolio.title || "",
                description: portfolio.description || "",
                visualContent: portfolio.visualContent || "",
                yourRole: portfolio.yourRole || "",
                skills: portfolio.skills || "",
                isDraft: portfolio.isDraft,
                situation: portfolio.situation || "",
                task: portfolio.task || "",
                action: portfolio.action || "",
                result: portfolio.result || "",
            });
            // Reset preview image to portfolio's image
            setPreviewImage(portfolio.visualContent || null);
        }
    };

    // Add to toggle edit mode to properly set previewImage
    const toggleEditMode = () => {
        setEditMode(true);
        if (portfolio && portfolio.visualContent) {
            setPreviewImage(portfolio.visualContent);
        }
    };

    const handleDeletePortfolio = () => {
        setDeleteDialogOpen(false);
        setLoading(true);
        api.delete(getBackendUrl(`/user-portfolio/${id}`))
            .then(res => {
                enqueueSnackbar("Portfolio deleted successfully", { variant: "success" });
                navigate(-1);
            })
            .catch(err => {
                console.error(err);
                enqueueSnackbar("Failed to delete portfolio", { variant: "error" });
            })
            .finally(() => {
                setLoading(false);
            });
    };

    if (!portfolio) {
        return (
            <Box p={4}>
                <Typography color="textSecondary">No portfolio project available.</Typography>
            </Box>
        );
    }

    return (
        <Stack height="100%" overflow="auto">
            <Container maxWidth="lg" sx={{ py: 3 }}>
                <Paper elevation={1} sx={{ height: "100%" }}>
                    <Stack p={3} py={3}>
                        <Card variant="elevation" sx={{ boxShadow: 0 }}>
                            <CardContent>
                                {editMode ? (
                                    // Edit Mode Layout
                                    <form onSubmit={handleSubmit(onSubmit)}>
                                        <Stack gap={2}>
                                            <Stack mb={2} direction="row" justifyContent="space-between" alignItems="center">
                                                <Stack direction="row" alignItems="center" gap={1}>
                                                    <IconButton onClick={cancelEdit} sx={{ ml: -2 }}>
                                                        <Close />
                                                    </IconButton>
                                                    <Typography variant="h5" fontWeight="bold">Edit Portfolio</Typography>
                                                </Stack>
                                                <Stack direction="row" alignItems="center" gap={2}>
                                                    <Button
                                                        variant="outlined"
                                                        onClick={cancelEdit}
                                                        startIcon={<Close />}
                                                    >
                                                        Cancel
                                                    </Button>
                                                    <Button
                                                        variant="contained"
                                                        type="submit"
                                                        startIcon={<Save />}
                                                    >
                                                        Save
                                                    </Button>
                                                </Stack>
                                            </Stack>

                                            <Controller
                                                name="title"
                                                control={control}
                                                render={({ field }) => (
                                                    <TextField
                                                        {...field}
                                                        size="small"
                                                        label="Title"
                                                        fullWidth
                                                        error={!!errors.title}
                                                        helperText={errors.title?.message}
                                                    />
                                                )}
                                            />

                                            {/* Hidden file input */}
                                            <input
                                                type="file"
                                                accept="image/jpeg,image/png,image/jpg"
                                                style={{ display: 'none' }}
                                                ref={fileInputRef}
                                                onChange={handleFileUpload}
                                            />

                                            <Stack>
                                                {/* Image preview */}
                                                <Box my={1}>
                                                    {previewImage ? (
                                                        <CardMedia
                                                            component="img"
                                                            height={300}
                                                            width={1}
                                                            image={getServerImage(previewImage)}
                                                            alt={portfolio.title}
                                                        // onError={() => handleImageError(portfolio)}
                                                        />
                                                        // <Box
                                                        //     component="img"
                                                        //     src={previewImage}
                                                        //     alt="Portfolio Preview"
                                                        //     style={{ height: 300, width: 1, objectFit: 'contain' }}
                                                        // />
                                                    ) : (
                                                        <PlaceHolderImage height={300} width={1} />
                                                    )}
                                                </Box>
                                                {/* Image upload button */}
                                                <Controller
                                                    name="visualContent"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Box>
                                                            <Button
                                                                variant="contained"
                                                                startIcon={<CloudUpload />}
                                                                onClick={triggerFileInput}
                                                                fullWidth
                                                            >
                                                                Upload Portfolio Image
                                                            </Button>
                                                            {errors.visualContent && (
                                                                <Typography color="error" variant="caption">
                                                                    {errors.visualContent.message}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    )}
                                                />
                                            </Stack>



                                            <Controller
                                                name="description"
                                                control={control}
                                                render={({ field }) => (
                                                    <Box>
                                                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                                            Description
                                                        </Typography>
                                                        <CustomMDEditor
                                                            value={field.value}
                                                            onChange={field.onChange}
                                                        />
                                                        {errors.description && (
                                                            <Typography color="error" variant="caption">
                                                                {errors.description.message}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                )}
                                            />

                                            <Controller
                                                name="yourRole"
                                                control={control}
                                                render={({ field }) => (
                                                    <TextField
                                                        {...field}
                                                        size="small"
                                                        label="Your Role"
                                                        fullWidth
                                                        multiline
                                                        rows={3}
                                                        error={!!errors.yourRole}
                                                        helperText={errors.yourRole?.message}
                                                    />
                                                )}
                                            />

                                            <Controller
                                                name="skills"
                                                control={control}
                                                render={({ field }) => (
                                                    <Box>
                                                        <TextField
                                                            {...field}
                                                            size="small"
                                                            label="Skills (comma separated)"
                                                            fullWidth
                                                            placeholder="React, JavaScript, Node.js"
                                                            error={!!errors.skills}
                                                            helperText={errors.skills?.message || "Separate skills with commas or use the add button"}
                                                        />
                                                    </Box>
                                                )}
                                            />

                                            <Typography variant="subtitle1" fontWeight="bold" gutterBottom mt={2}>
                                                STAR Method
                                            </Typography>

                                            <Controller
                                                name="situation"
                                                control={control}
                                                render={({ field }) => (
                                                    <TextField
                                                        {...field}
                                                        size="small"
                                                        label="Situation"
                                                        fullWidth
                                                        multiline
                                                        rows={3}
                                                        error={!!errors.situation}
                                                        helperText={errors.situation?.message}
                                                    />
                                                )}
                                            />

                                            <Controller
                                                name="task"
                                                control={control}
                                                render={({ field }) => (
                                                    <TextField
                                                        {...field}
                                                        size="small"
                                                        label="Task"
                                                        fullWidth
                                                        multiline
                                                        rows={3}
                                                        error={!!errors.task}
                                                        helperText={errors.task?.message}
                                                    />
                                                )}
                                            />

                                            <Controller
                                                name="action"
                                                control={control}
                                                render={({ field }) => (
                                                    <TextField
                                                        {...field}
                                                        size="small"
                                                        label="Action"
                                                        fullWidth
                                                        multiline
                                                        rows={3}
                                                        error={!!errors.action}
                                                        helperText={errors.action?.message}
                                                    />
                                                )}
                                            />

                                            <Controller
                                                name="result"
                                                control={control}
                                                render={({ field }) => (
                                                    <TextField
                                                        {...field}
                                                        size="small"
                                                        label="Result"
                                                        fullWidth
                                                        multiline
                                                        rows={3}
                                                        error={!!errors.result}
                                                        helperText={errors.result?.message}
                                                    />
                                                )}
                                            />

                                            <Stack direction="row" alignItems="flex-end" justifyContent="space-between" gap={1}>
                                                <Box sx={{ alignSelf: "flex-start" }}>
                                                    <Controller
                                                        name="isDraft"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <Box display="flex"
                                                                alignItems="center"
                                                                onClick={() => field.onChange(!field.value)}
                                                                sx={{
                                                                    cursor: 'pointer'
                                                                }}>
                                                                <Chip
                                                                    label={field.value ? "Draft" : "Published"}
                                                                    color={field.value ? "default" : "primary"}
                                                                    onClick={() => field.onChange(!field.value)}
                                                                />
                                                                <Typography variant="caption" sx={{ ml: 1 }}>
                                                                    {field.value ? "Click to publish" : "Click to set as draft"}
                                                                </Typography>
                                                            </Box>
                                                        )}
                                                    />
                                                </Box>
                                                <Button variant="outlined" color="error" onClick={() => setDeleteDialogOpen(true)}>
                                                    Delete
                                                </Button>
                                            </Stack>
                                        </Stack>
                                    </form>
                                ) : (
                                    // View Mode Layout
                                    <Stack gap={1}>
                                        <Stack mb={2} direction="row" justifyContent="space-between" alignItems="center">
                                            <Stack direction="row" alignItems="center" gap={1}>
                                                <IconButton onClick={() => navigate(-1)} sx={{ ml: -2 }}>
                                                    <ArrowBack />
                                                </IconButton>
                                                <Typography variant="h5" fontWeight="bold">{portfolio.title ?? "Untitled"}</Typography>
                                            </Stack>

                                            <Stack direction="row" alignItems="center" gap={1}>
                                                <Chip
                                                    label={portfolio.isDraft ? "Draft" : "Published"}
                                                    color={portfolio.isDraft ? "default" : "primary"}
                                                />
                                                {/* edit button */}
                                                {
                                                    (isAdmin || isOwner) && (
                                                        <IconButton size="small" onClick={toggleEditMode}>
                                                            <Edit />
                                                        </IconButton>
                                                    )
                                                }
                                            </Stack>
                                        </Stack>

                                        {portfolio.visualContent ? (
                                            <Box my={2}>
                                                <CardMedia
                                                    component="img"
                                                    height={300}
                                                    width={1}
                                                    image={getServerImage(portfolio.visualContent)}
                                                    alt={portfolio.title}
                                                // onError={() => handleImageError(portfolio)}
                                                />
                                            </Box>
                                        ) : <PlaceHolderImage height={300} width={1} />
                                        }

                                        <CustomMDPreview description={portfolio.description ?? "No description available"} />

                                        <Typography mt={4} variant="subtitle2" fontWeight="bold" gutterBottom>
                                            Your Role:
                                        </Typography>
                                        <Typography variant="body2" paragraph>
                                            {getNullableString(portfolio.yourRole)}
                                        </Typography>

                                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                                            Skills:
                                        </Typography>
                                        <Box display="flex" flexWrap="wrap" gap={1} mb={1}>
                                            {
                                                !portfolio.skills ? "-" :
                                                    portfolio.skills.split(',').map((skill, i) => <Chip key={i} label={skill.trim()} size="small" />)
                                            }
                                        </Box>

                                        <Divider sx={{ my: 2 }} />
                                        <Typography variant="subtitle1" my={2}>
                                            STAR Method
                                        </Typography>

                                        <Stack gap={2}>
                                            <Box>
                                                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                                                    Situation:
                                                </Typography>
                                                <Typography variant="body2" paragraph>
                                                    {getNullableString(portfolio.situation)}
                                                </Typography>
                                            </Box>

                                            <Box>
                                                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                                                    Task:
                                                </Typography>
                                                <Typography variant="body2" paragraph>
                                                    {getNullableString(portfolio.task)}
                                                </Typography>
                                            </Box>

                                            <Box>
                                                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                                                    Action:
                                                </Typography>
                                                <Typography variant="body2" paragraph>
                                                    {getNullableString(portfolio.action)}
                                                </Typography>
                                            </Box>

                                            <Box>
                                                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                                                    Result:
                                                </Typography>
                                                <Typography variant="body2" paragraph>
                                                    {getNullableString(portfolio.result)}
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </Stack>
                                )}
                                <ConfirmationDialog
                                    open={deleteDialogOpen}
                                    title="Delete Portfolio"
                                    message="Are you sure you want to delete this portfolio?"
                                    onConfirm={() => handleDeletePortfolio()}
                                    onCancel={() => setDeleteDialogOpen(false)}
                                    severity="error"
                                />
                            </CardContent>
                        </Card>
                    </Stack >
                </Paper >
            </Container >
        </Stack >
    );
};

export default Portfolio;   
